import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { trackPageView } from './utils/analytics';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeChat from './pages/EmployeeChat';
import Brain from './pages/Brain';
import Tasks from './pages/Tasks';
import Integrations from './pages/Integrations';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import Team from './pages/Team';
import Analytics from './pages/Analytics';
import APIPage from './pages/APIPage';
import AcceptInvite from './pages/AcceptInvite';
import TrelloCallback from './pages/TrelloCallback';
import AuthCallback from './pages/AuthCallback';
import StudentPlan from './pages/StudentPlan';
import GiftCards from './pages/GiftCards';
import PresentationBuilder from './pages/PresentationBuilder';

// Static Pages - Product
import AIEmployees from './pages/static/AIEmployees';
import Features from './pages/static/Features';
import Pricing from './pages/static/Pricing';
import IntegrationsPage from './pages/static/IntegrationsPage';
import API from './pages/static/API';

// Static Pages - Company
import About from './pages/static/About';
import Blog from './pages/static/Blog';
import BlogPost from './pages/static/BlogPost';
import Careers from './pages/static/Careers';
import Press from './pages/static/Press';
import Partners from './pages/static/Partners';
import Contact from './pages/static/Contact';

// Static Pages - Resources
import Documentation from './pages/static/Documentation';
import HelpCenter from './pages/static/HelpCenter';
import Community from './pages/static/Community';
import Templates from './pages/static/Templates';
import Status from './pages/static/Status';
import Roadmap from './pages/static/Roadmap';

// Static Pages - Legal
import Privacy from './pages/static/Privacy';
import Terms from './pages/static/Terms';
import Security from './pages/static/Security';
import Cookies from './pages/static/Cookies';
import GDPR from './pages/static/GDPR';
import RefundPolicy from './pages/static/RefundPolicy';

// Layouts
import DashboardLayout from './components/dashboard/DashboardLayout';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import PaymentSettings from './pages/admin/PaymentSettings';
import SiteSettings from './pages/admin/SiteSettings';
import BlogManagement from './pages/admin/BlogManagement';
import UserManagement from './pages/admin/UserManagement';
import RoadmapManagement from './pages/admin/RoadmapManagement';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { checkAuth } = useAuthStore();
  const location = useLocation();

  // Check authentication status on app mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/callback/trello" element={<TrelloCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Static Pages - Product */}
      <Route path="/employees" element={<AIEmployees />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/api" element={<API />} />

      {/* Static Pages - Company */}
      <Route path="/about" element={<About />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/press" element={<Press />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/contact" element={<Contact />} />

      {/* Static Pages - Resources */}
      <Route path="/docs" element={<Documentation />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/community" element={<Community />} />
      <Route path="/templates" element={<Templates />} />
      <Route path="/status" element={<Status />} />
      <Route path="/roadmap" element={<Roadmap />} />

      {/* Static Pages - Legal */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/security" element={<Security />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/gdpr" element={<GDPR />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="employees" element={<Employees />} />
        <Route path="employees/:employeeId" element={<EmployeeChat />} />
        <Route path="brain" element={<Brain />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="team" element={<Team />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="api" element={<APIPage />} />
        <Route path="billing" element={<Billing />} />
        <Route path="student-plan" element={<StudentPlan />} />
        <Route path="gift-cards" element={<GiftCards />} />
        <Route path="settings" element={<Settings />} />
        <Route path="presentations/:presentationId" element={<PresentationBuilder />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="blog" element={<BlogManagement />} />
        <Route path="roadmap" element={<RoadmapManagement />} />
        <Route path="payments" element={<PaymentSettings />} />
        <Route path="settings" element={<SiteSettings />} />
      </Route>

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-dark-900 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-bold gradient-text mb-4">404</h1>
              <p className="text-text-secondary mb-8">Page not found</p>
              <a href="/" className="btn-primary">
                Go Home
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}
